import React, { FC, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useAuth } from "go-security/services/auth";
import { selectOrganization } from "go-security/services/organizations/selectors";
import Search from "../../../../../../../../../../../components/Common/Search/Search";
import {
	VenueSettingsApi,
	VenueSettingsKdsItemApi,
	WeightBarcodeApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../services/Api/api";
import { VenueSettingsItemApi } from "../../../../../../../../../../../services/Api/types";
import { getApiURL } from "../../../../../../../../../../../utils/envutil";
import {
	getOptionsByVenueSetting,
	getParsedVenueSettings,
	getSettingsDriversTypes,
	getSettingsKioskBasicTypes,
	getSettingsKioskTypes,
	getSettingsOtherSaleTypes,
	getSettingsOtherTypes,
	getSettingsPaymentsAndReceiptsTypes,
	getSettingsPreparationStatusesTypes,
	getSettingsPrintoutTypes,
	getSettingsReportTypes,
	getSettingsSaleEditTypes,
	getSettingsSaleOpenTypes,
	getSettingsStockTypes,
	getSettingsTypes,
} from "../utils";
import { DriversCard } from "./DriversCard";
import EditSalesSettingsCard from "./EditSalesSettingsCard";
import ExternalStockCard from "./ExternalStockCard";
import KioskSettingsCard from "./KioskSettings/KioskSettingsCard";
import LimitDirectionsCard from "./LimitDirectionsCard";
import LiveOrdersSettingsCard from "./LiveOrdersSettingsCard";
import OpenSalesSettingsCard from "./OpenSalesSettingsCard";
import OtherSalesSettingsCard from "./OtherSalesSettingsCard";
import OtherSettingsCard from "./OtherSettingsCard";
import PaymentsAndReceiptsCard from "./PaymentsCard";
import PreparationStatusesCard from "./PreparationStatusesCard";
import PrintoutTemplatesCard from "./PrintoutTemplatesCard";
import ReportSettingsCard from "./ReportSettingsCard";
import WeightedBarcodesCard from "./WeightedBarcodesCard";

interface Props {
	settings: VenueSettingsApi;
	barcodeSettings: string[];
}

const VenueSettingsForm: FC<Props> = ({ settings, barcodeSettings }) => {
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const auth = useAuth(getApiURL());
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const allSettingsOptions = [
		...getSettingsReportTypes(settings.settings),
		...getSettingsSaleOpenTypes(settings.settings),
		...getSettingsSaleEditTypes(settings.settings),
		...getSettingsOtherSaleTypes(settings.settings),
		...getSettingsOtherTypes(settings.settings),
		...getSettingsPaymentsAndReceiptsTypes(settings.settings),
		...getSettingsPreparationStatusesTypes(settings.settings),
		...getSettingsStockTypes(settings.settings),
		...getSettingsPrintoutTypes(settings.settings),
		...getSettingsDriversTypes(settings.settings),
		...getSettingsKioskTypes(settings.settings),
		...getSettingsKioskBasicTypes(settings.settings),
	].filter((setting) => !setting.type.includes("?"));
	const form = useForm<VenueSettingsApi>({
		criteriaMode: "all",
		defaultValues: getParsedVenueSettings(settings, allSettingsOptions, organization.languages || []),
		shouldUnregister: false,
	});
	const [search, setSearch] = useState("");
	const { handleSubmit, reset, setError, formState, control, clearErrors } = form;
	const { fields } = useFieldArray({
		control,
		name: "kds_settings",
		keyName: "key",
	});

	const onSearch = (name: string) => {
		setSearch(name);
	};

	const getParsedFormData = (data: VenueSettingsApi) => {
		const newData = JSON.parse(JSON.stringify(data));
		newData.kds_settings.forEach((kds: VenueSettingsKdsItemApi) => {
			kds.devices = [];
		});
		newData.weight_barcodes = newData.weight_barcodes.map((bc: WeightBarcodeApi) => `${bc.type}${bc.value}`);
		newData.settings = newData.settings.map((x: VenueSettingsItemApi) => {
			const cnf = getOptionsByVenueSetting(x.default_value ? x.default_value : "", x.type, t);
			if (
				!cnf.map((x) => x.value).includes(x.value ? x.value : "") &&
				x.type !== "DRIVER_CAN_CHANGE_STATUS_TO_DELIVERED_FROM_METERS" &&
				x.type !== "CITY_SEARCH_DISTANCE_KM" &&
				x.type !== "USER_LOGOUT_THRESHOLD" &&
				x.type !== "KDS_FLASH_SECONDS"
			) {
				x.value = undefined;
			}

			if (x.type === "DIRECTION_PRINTOUT_ID") {
				x.value = data.direction_printout?.id.toString();
			}

			if (
				x.type === "DRIVER_CAN_CHANGE_STATUS_TO_DELIVERED_FROM_METERS" &&
				(x.value === null || x.value === "")
			) {
				delete x.value;
			}

			if (
				(x.type === "CITY_SEARCH_DISTANCE_KM" ||
					x.type === "USER_LOGOUT_THRESHOLD" ||
					x.type === "KDS_FLASH_SECONDS") &&
				(x.value === null || x.value === "" || x.value === "empty")
			) {
				delete x.value;
			}
			return x.value === "empty" ? { ...x, value: null } : x;
		});

		newData.preparation_statuses =
			data.supported_preparation_statuses.values.length > 0
				? data.supported_preparation_statuses.values
				: data.supported_preparation_statuses.default_values;
		delete newData.supported_preparation_statuses;

		return newData;
	};

	const onSubmit = handleSubmit(async (data: VenueSettingsApi) => {
		const newData = getParsedFormData(data);
		setLoading(true);
		try {
			const res = await api.organization().updateVenueSettings(newData);
			reset(getParsedVenueSettings(res, allSettingsOptions, organization.languages || []));
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));

			await auth.loginOrganization(Number(organization?.id));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty
			formState={formState}
			loading={loading}
			onSubmit={(data) => {
				clearErrors();
				onSubmit(data);
			}}
		>
			<div className={"d-flex flex-column"}>
				<Search onSearch={onSearch} placeholder={t("modules.venue_settings.action.search.title")} />
				<PreparationStatusesCard
					fields={fields}
					allSettingsOptions={allSettingsOptions}
					form={form}
					search={search}
					title={t("modules.venue_settings.field.statuses.title")}
					types={getSettingsTypes(
						getSettingsPreparationStatusesTypes(settings.settings, true, t, organization.id),
						t,
						search
					)}
				/>
				<LimitDirectionsCard
					fields={fields}
					title={t("modules.venue_settings.field.printout.title")}
					form={form}
				/>
				<ExternalStockCard
					types={getSettingsTypes(getSettingsStockTypes(settings.settings), t, search)}
					form={form}
					title={t("modules.venue_settings.field.internal_stock.title")}
					search={search}
				/>
				<PrintoutTemplatesCard title={t("modules.venue_settings.field.printout_templates.title")} form={form} />
				<ReportSettingsCard
					search={search}
					title={t("modules.venue_settings.field.report.title")}
					form={form}
					types={getSettingsTypes(getSettingsReportTypes(settings.settings), t, search)}
				/>
				<OpenSalesSettingsCard
					types={getSettingsTypes(getSettingsSaleOpenTypes(settings.settings), t, search)}
					search={search}
					title={t("modules.venue_settings.field.open_sales.title")}
					form={form}
				/>
				<EditSalesSettingsCard
					search={search}
					title={t("modules.venue_settings.field.edit_sales.title")}
					form={form}
					types={getSettingsTypes(getSettingsSaleEditTypes(settings.settings), t, search)}
				/>
				<WeightedBarcodesCard
					weightBarcodesConfig={barcodeSettings}
					title={t("modules.venue_settings.field.pos.title")}
					form={form}
				/>
				<PaymentsAndReceiptsCard
					types={getSettingsTypes(getSettingsPaymentsAndReceiptsTypes(settings.settings), t, search)}
					form={form}
					title={t("modules.venue_settings.field.payments_and_receipts.title")}
					search={search}
				/>
				<DriversCard
					types={getSettingsTypes(getSettingsDriversTypes(settings.settings), t, search)}
					form={form}
					title={t("modules.venue_settings.field.drivers.title")}
					search={search}
				/>
				<LiveOrdersSettingsCard form={form} />
				<KioskSettingsCard form={form} search={search} settings={settings.settings} />
				<OtherSalesSettingsCard
					search={search}
					title={t("modules.venue_settings.field.other_sales.title")}
					form={form}
					types={getSettingsTypes(getSettingsOtherSaleTypes(settings.settings), t, search)}
				/>
				<OtherSettingsCard
					search={search}
					title={t("modules.venue_settings.field.other.title")}
					form={form}
					types={getSettingsTypes(getSettingsOtherTypes(settings.settings), t, search)}
				/>
				<hr />
			</div>
		</FormDirty>
	);
};
export default VenueSettingsForm;

// t("enums.venue_settings.RECEIPT_EMAIL.label");
// t("enums.venue_settings.RECEIPT_SMS.label");
// t("enums.venue_settings.RECEIPT_QR_CODE.label");
// t("enums.venue_settings.RECEIPT_PRINTOUT.label");
// t("enums.venue_settings.KDS_MERGE_ITEMS.label");
// t("enums.venue_settings.SALE_STATUS_PREPERATION_DEFAULT.label");
// t("enums.venue_settings.SALE_BILL_SET_STATUS_PREPARATION.label");
// t("enums.venue_settings.NOTIFICATION_STATUS_PREPERATION.label");
// t("enums.venue_settings.KDS_NUMBER_ENABLED.label");
// t("enums.venue_settings.STOCK_TRACKING.label");
// t("enums.venue_settings.SALE_EMPTY_STOCK.label");
// t("enums.venue_settings.REPORT_TYPE.label");
// t("enums.venue_settings.REPORT_POS_SETTLEMENT.label");
// t("enums.venue_settings.REPORT_AUTO_START_AMOUNT.label");
// t("enums.venue_settings.REPORT_REMOVES_BILLS_AFTER_OPEN.label");
// t("enums.venue_settings.SALE_DEFAULT_BUTTON.label");
// t("enums.venue_settings.SALE_BILL_TYPE_BUTTON.label");
// t("enums.venue_settings.SALE_QUICK_BUTTON.label");
// t("enums.venue_settings.SALE_CLIENT_BUTTON.label");
// t("enums.venue_settings.SALE_COMMENT_BUTTON.label");
// t("enums.venue_settings.SALE_GROUP_BUTTON.label");
// t("enums.venue_settings.SALE_GUEST_NUMBER_POPUP.label");
// t("enums.venue_settings.SALE_SEAT.label");
// t("enums.venue_settings.SALE_GUEST_NUMBER_BUTTON.label");
// t("enums.venue_settings.SALE_CLIENT_OPTION.label");
// t("enums.venue_settings.SALE_CLIENT_CARD_OPTION.label");
// t("enums.venue_settings.SALE_TABLE_OPTION.label");
// t("enums.venue_settings.SALE_OPEN_CASH_DRAWER.label");
// t("enums.venue_settings.SALE_DESCRIPTION_OPTION.label");
// t("enums.venue_settings.SALE_SPLIT_OPTION.label");
// t("enums.venue_settings.SALE_PRINT_INITIAL_OPTION.label");
// t("enums.venue_settings.SALE_RESEND_TO_KITCHEN.label");
// t("enums.venue_settings.SALE_DETERMINED_PRICE_OPTION.label");
// t("enums.venue_settings.SALE_CLIENT_BARCODE_OPTION.label");
// t("enums.venue_settings.SALE_MENU_GRID_OPTION.label");
// t("enums.venue_settings.SALE_MENU_SEARCH_OPTION.label");
// t("enums.venue_settings.SALE_MENU_SKU_OPTION.label");
// t("enums.venue_settings.SALE_OPEN_BILLS.label");
// t("enums.venue_settings.SALE_NOT_AVAILABLE_PRODUCTS.label");
// t("enums.venue_settings.SALE_SAVE_ON_CLOSE.label");
// t("enums.venue_settings.SALE_STORNO_REASON.label");
// t("enums.venue_settings.SALE_TAX_ID_NO.label");
// t("enums.venue_settings.SALE_AUTO_RELEASE.label");
// t("enums.venue_settings.SCANNER_TYPE.label");
// t("enums.venue_settings.TRANSFER_SAFE_MODE.label");
// t("enums.venue_settings.SALE_LOGOUT_AFTER_CLOSE_BILL.label");
// t("enums.venue_settings.USER_LOGOUT_THRESHOLD.label");
// t("enums.venue_settings.RESERVATION.label");
// t("enums.venue_settings.SALE_PRINT_PAYMENT_TERMINAL_CONFIRMATION.label");
// t("enums.venue_settings.IS_LOCATION_REQUIRED.label");
// t("enums.venue_settings.DRIVER_CAN_ASSIGN_ITSELF.label");
// t("enums.venue_settings.DRIVER_CAN_CHANGE_STATUS_FROM_STATUS.label");
// t("enums.venue_settings.DRIVER_CAN_CHANGE_STATUS_TO_DELIVERED_FROM_METERS.label");
// t("enums.venue_settings.SALE_VALIDATE_DELIVERY_EMPLOYEE.label");
// t("enums.venue_settings.SALE_VALIDATE_ORDER_TYPE.label");
// t("enums.venue_settings.KDS_FLASH_SECONDS.label");
// t("enums.venue_settings.SALE_ORDER_ITEM_COURSE_HEADER.label");
// t("enums.venue_settings.CITY_SEARCH_DISTANCE_KM.label");
// t("enums.venue_settings.SALE_FORCE_ORDER_FISCALIZATION.label");
// t("enums.venue_settings.PRINT_COMPANY_ON_PDF.label");
// t("enums.venue_settings.PRICE_LIST_COMPATIBILITY_MODE.label");
// t("enums.venue_settings.FORCE_KEEP_SCREEN_ON.label");
// t("enums.venue_settings.SALE_QUICK_SET_ADD.label");
// t("enums.venue_settings.MONITOR_BATTERY_LEVEL.label");
// t("enums.venue_settings.MENU_DISPLAY_TRANSLATIONS.label");
// t("enums.venue_settings.KIOSK_ANIMATION_LIST_ENABLE.label");
// t("enums.venue_settings.KIOSK_ANIMATION_CHANGE_VIEW_ENABLE.label");
// t("enums.venue_settings.KIOSK_BLUR_BACKGROUND_ENABLE.label");
// t("enums.venue_settings.KIOSK_LOW_PERFORMANCE_BLUR_ENABLE.label");
// t("enums.venue_settings.KIOSK_CHANGE_CONFIRM_BUTTON_COLOR_ENABLE.label");
// t("enums.venue_settings.KIOSK_ENABLE.label");
// t("enums.venue_settings.VOUCHER_FEATURE_ENABLED.label");
// t("enums.venue_settings.ERECEIPT_FEATURE_ENABLED.label");
// t("enums.venue_settings.CASH_PAYMENT_FEATURE_ENABLED.label");
// t("enums.venue_settings.MODIFIER_GROUPS_EXPAND_TYPE.label");
// t("enums.venue_settings.RECEIPT_EMAIL.desc");
// t("enums.venue_settings.RECEIPT_SMS.desc");
// t("enums.venue_settings.RECEIPT_QR_CODE.desc");
// t("enums.venue_settings.RECEIPT_PRINTOUT.desc");
// t("enums.venue_settings.SALE_ORDER_ITEM_COURSE_HEADER.desc");
// t("enums.venue_settings.KDS_MERGE_ITEMS.desc");
// t("enums.venue_settings.SALE_STATUS_PREPERATION_DEFAULT.desc");
// t("enums.venue_settings.SALE_BILL_SET_STATUS_PREPARATION.desc");
// t("enums.venue_settings.NOTIFICATION_STATUS_PREPERATION.desc");
// t("enums.venue_settings.KDS_NUMBER_ENABLED.desc");
// t("enums.venue_settings.STOCK_TRACKING.desc");
// t("enums.venue_settings.SALE_EMPTY_STOCK.desc");
// t("enums.venue_settings.REPORT_TYPE.desc");
// t("enums.venue_settings.REPORT_POS_SETTLEMENT.desc");
// t("enums.venue_settings.REPORT_AUTO_START_AMOUNT.desc");
// t("enums.venue_settings.REPORT_REMOVES_BILLS_AFTER_OPEN.desc");
// t("enums.venue_settings.SALE_DEFAULT_BUTTON.desc");
// t("enums.venue_settings.SALE_BILL_TYPE_BUTTON.desc");
// t("enums.venue_settings.SALE_QUICK_BUTTON.desc");
// t("enums.venue_settings.SALE_CLIENT_BUTTON.desc");
// t("enums.venue_settings.SALE_COMMENT_BUTTON.desc");
// t("enums.venue_settings.SALE_GROUP_BUTTON.desc");
// t("enums.venue_settings.SALE_GUEST_NUMBER_POPUP.desc");
// t("enums.venue_settings.SALE_SEAT.desc");
// t("enums.venue_settings.SALE_GUEST_NUMBER_BUTTON.desc");
// t("enums.venue_settings.SALE_CLIENT_OPTION.desc");
// t("enums.venue_settings.SALE_CLIENT_CARD_OPTION.desc");
// t("enums.venue_settings.SALE_TABLE_OPTION.desc");
// t("enums.venue_settings.SALE_OPEN_CASH_DRAWER.desc");
// t("enums.venue_settings.SALE_DESCRIPTION_OPTION.desc");
// t("enums.venue_settings.SALE_SPLIT_OPTION.desc");
// t("enums.venue_settings.SALE_PRINT_INITIAL_OPTION.desc");
// t("enums.venue_settings.SALE_RESEND_TO_KITCHEN.desc");
// t("enums.venue_settings.SALE_DETERMINED_PRICE_OPTION.desc");
// t("enums.venue_settings.SALE_CLIENT_BARCODE_OPTION.desc");
// t("enums.venue_settings.SALE_MENU_GRID_OPTION.desc");
// t("enums.venue_settings.SALE_MENU_SEARCH_OPTION.desc");
// t("enums.venue_settings.SALE_MENU_SKU_OPTION.desc");
// t("enums.venue_settings.SALE_OPEN_BILLS.desc");
// t("enums.venue_settings.SALE_NOT_AVAILABLE_PRODUCTS.desc");
// t("enums.venue_settings.SALE_SAVE_ON_CLOSE.desc");
// t("enums.venue_settings.SALE_STORNO_REASON.desc");
// t("enums.venue_settings.SALE_TAX_ID_NO.desc");
// t("enums.venue_settings.SALE_AUTO_RELEASE.desc");
// t("enums.venue_settings.SCANNER_TYPE.desc");
// t("enums.venue_settings.TRANSFER_SAFE_MODE.desc");
// t("enums.venue_settings.SALE_LOGOUT_AFTER_CLOSE_BILL.desc");
// t("enums.venue_settings.USER_LOGOUT_THRESHOLD.desc");
// t("enums.venue_settings.RESERVATION.desc");
// t("enums.venue_settings.SALE_PRINT_PAYMENT_TERMINAL_CONFIRMATION.desc");
// t("enums.venue_settings.IS_LOCATION_REQUIRED.desc");
// t("enums.venue_settings.DRIVER_CAN_ASSIGN_ITSELF.desc");
// t("enums.venue_settings.DRIVER_CAN_CHANGE_STATUS_FROM_STATUS.desc");
// t("enums.venue_settings.DRIVER_CAN_CHANGE_STATUS_TO_DELIVERED_FROM_METERS.desc");
// t("enums.venue_settings.SALE_VALIDATE_DELIVERY_EMPLOYEE.desc");
// t("enums.venue_settings.SALE_VALIDATE_ORDER_TYPE.desc");
// t("enums.venue_settings.KDS_FLASH_SECONDS.desc");
// t("enums.venue_settings.CITY_SEARCH_DISTANCE_KM.desc");
// t("enums.venue_settings.SALE_FORCE_ORDER_FISCALIZATION.desc");
// t("enums.venue_settings.PRINT_COMPANY_ON_PDF.desc");
// t("enums.venue_settings.PRICE_LIST_COMPATIBILITY_MODE.desc");
// t("enums.venue_settings.FORCE_KEEP_SCREEN_ON.desc");
// t("enums.venue_settings.SALE_QUICK_SET_ADD.desc");
// t("enums.venue_settings.MONITOR_BATTERY_LEVEL.desc");
// t("enums.venue_settings.MENU_DISPLAY_TRANSLATIONS.desc");
// t("enums.venue_settings.KIOSK_ANIMATION_LIST_ENABLE.desc");
// t("enums.venue_settings.KIOSK_ANIMATION_CHANGE_VIEW_ENABLE.desc");
// t("enums.venue_settings.KIOSK_BLUR_BACKGROUND_ENABLE.desc");
// t("enums.venue_settings.KIOSK_LOW_PERFORMANCE_BLUR_ENABLE.desc");
// t("enums.venue_settings.KIOSK_CHANGE_CONFIRM_BUTTON_COLOR_ENABLE.desc");
// t("enums.venue_settings.KIOSK_ENABLE.desc");
// t("enums.venue_settings.VOUCHER_FEATURE_ENABLED.desc");
// t("enums.venue_settings.ERECEIPT_FEATURE_ENABLED.desc");
// t("enums.venue_settings.CASH_PAYMENT_FEATURE_ENABLED.desc");
// t("enums.venue_settings.MODIFIER_GROUPS_EXPAND_TYPE.desc");
